import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  ArrowLeft, Shield, ShieldCheck, MapPin, FileCheck, Scale, Landmark,
  FolderCheck, UserCheck, AlertTriangle, ChevronRight, Building2, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PropertyTimeline from "@/components/PropertyTimeline";

const BADGE_ICONS = {
  "shield-check": ShieldCheck, "user-check": UserCheck, "file-check": FileCheck,
  "scale": Scale, "map-pin": MapPin, "landmark": Landmark, "folder-check": FolderCheck,
  "badge": Shield,
};

const LIFECYCLE_COLORS = ["#71717A","#D4AF37","#F59E0B","#F59E0B","#556B2F","#F59E0B","#556B2F","#D4AF37","#D4AF37","#F59E0B","#556B2F","#71717A"];

export default function PropertyDetail() {
  const { propertyId } = useParams();
  const { API } = useAuth();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [risk, setRisk] = useState(null);
  const [badges, setBadges] = useState([]);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [lifecycleStates, setLifecycleStates] = useState({});
  const [showLifecycle, setShowLifecycle] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [lifecycleNote, setLifecycleNote] = useState("");
  const [selectedBadge, setSelectedBadge] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, rRes, bRes, lRes, abRes] = await Promise.all([
        fetch(`${API}/properties`, { credentials: "include" }),
        fetch(`${API}/properties/${propertyId}/risk`, { credentials: "include" }),
        fetch(`${API}/properties/${propertyId}/badges`, { credentials: "include" }),
        fetch(`${API}/lifecycle/states`, { credentials: "include" }),
        fetch(`${API}/badges/available`, { credentials: "include" }),
      ]);
      if (pRes.ok) {
        const props = await pRes.json();
        setProperty(props.find(p => p.property_id === propertyId));
      }
      if (rRes.ok) setRisk(await rRes.json());
      if (bRes.ok) setBadges(await bRes.json());
      if (lRes.ok) setLifecycleStates(await lRes.json());
      if (abRes.ok) setAvailableBadges(await abRes.json());
    } catch (e) { console.error(e); }
  }, [API, propertyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateLifecycle = async () => {
    try {
      await fetch(`${API}/properties/${propertyId}/lifecycle`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ state: parseInt(selectedState), note: lifecycleNote }),
      });
      setShowLifecycle(false);
      setSelectedState("");
      setLifecycleNote("");
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const addBadge = async () => {
    try {
      await fetch(`${API}/properties/${propertyId}/badges`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ badge: selectedBadge }),
      });
      setShowBadge(false);
      setSelectedBadge("");
      fetchAll();
    } catch (e) { console.error(e); }
  };

  if (!property) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentState = property.lifecycle_state ?? 0;
  const riskScore = risk?.risk_score ?? 0;
  const riskColor = riskScore <= 30 ? "#556B2F" : riskScore <= 60 ? "#F59E0B" : "#ef4444";

  return (
    <div data-testid="property-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button data-testid="back-to-properties" onClick={() => navigate("/dashboard/properties")} className="text-white/40 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="font-serif text-2xl text-white">{property.name}</h2>
          <p className="text-white/40 text-sm font-sans">{property.address}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="update-lifecycle-btn" onClick={() => setShowLifecycle(true)} variant="outline" className="border-white/10 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm text-xs">
            Update State
          </Button>
          <Button data-testid="add-badge-btn" onClick={() => setShowBadge(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm text-xs">
            Add Badge
          </Button>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="flex flex-wrap gap-2 mb-6">
        {badges.map(b => {
          const Icon = BADGE_ICONS[b.icon] || Shield;
          return (
            <div key={b.key} className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-sans" style={{ borderColor: b.color + "40", backgroundColor: b.color + "10", color: b.color }}>
              <Icon className="w-3 h-3" />
              {b.label}
            </div>
          );
        })}
        {badges.length === 0 && <span className="text-white/20 text-xs font-sans">No badges yet</span>}
      </div>

      {/* Top Row: Survey + Risk + Lifecycle State */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Survey Info */}
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-5">
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Survey Records</p>
          <div className="space-y-2 text-sm font-sans">
            {property.district && <div className="flex justify-between"><span className="text-white/40">District</span><span className="text-white">{property.district}</span></div>}
            {property.taluka && <div className="flex justify-between"><span className="text-white/40">Taluka</span><span className="text-white">{property.taluka}</span></div>}
            {property.village && <div className="flex justify-between"><span className="text-white/40">Village</span><span className="text-white">{property.village}</span></div>}
            {property.survey_number && <div className="flex justify-between"><span className="text-white/40">Survey No.</span><span className="text-white">{property.survey_number}{property.sub_division ? `/${property.sub_division}` : ""}</span></div>}
            <div className="flex justify-between"><span className="text-white/40">Land Type</span><span className="text-white capitalize">{property.land_type || property.property_type}</span></div>
            <div className="flex justify-between"><span className="text-white/40">Area</span><span className="text-white">{property.area_sqft?.toLocaleString()} sqft</span></div>
            <div className="flex justify-between"><span className="text-white/40">Valuation</span><span className="text-white">${property.valuation?.toLocaleString()}</span></div>
          </div>
        </div>

        {/* Risk Score */}
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-5">
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Risk Assessment</p>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative w-20 h-20">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={riskColor} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(riskScore / 100) * 264} 264`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-serif text-xl" style={{ color: riskColor }}>{riskScore}</span>
                <span className="text-white/30 text-[8px] font-sans uppercase">/100</span>
              </div>
            </div>
            <div>
              <p className="font-sans font-medium text-sm capitalize" style={{ color: riskColor }}>{risk?.risk_level || "N/A"} Risk</p>
              <p className="text-white/40 text-[10px] font-sans mt-1">Lower is better</p>
            </div>
          </div>
          {risk?.dimensions && (
            <div className="space-y-1.5">
              {Object.entries(risk.dimensions).map(([key, dim]) => (
                <div key={key} className="flex items-center justify-between text-[10px] font-sans">
                  <span className="text-white/40 capitalize">{key.replace(/_/g, " ")}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${dim.score}%`, backgroundColor: dim.score <= 30 ? "#556B2F" : dim.score <= 60 ? "#F59E0B" : "#ef4444" }} />
                    </div>
                    <span className="text-white/50 w-6 text-right">{dim.score}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Lifecycle State */}
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-5">
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Lifecycle State</p>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center border" style={{ borderColor: LIFECYCLE_COLORS[currentState] + "40", backgroundColor: LIFECYCLE_COLORS[currentState] + "15" }}>
              <span className="font-serif text-lg" style={{ color: LIFECYCLE_COLORS[currentState] }}>{currentState}</span>
            </div>
            <div>
              <p className="text-white font-sans font-medium text-sm">{lifecycleStates[currentState]?.label || `State ${currentState}`}</p>
              <p className="text-white/40 text-[10px] font-sans">{lifecycleStates[currentState]?.description}</p>
            </div>
          </div>
          {/* Mini progress */}
          <div className="flex gap-0.5">
            {Array.from({length: 12}, (_, i) => (
              <div key={i} className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: i <= currentState ? LIFECYCLE_COLORS[i] : "rgba(255,255,255,0.05)" }} />
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-white/20 text-[8px] font-sans">Unclaimed</span>
            <span className="text-white/20 text-[8px] font-sans">Post-Sale</span>
          </div>
        </div>
      </div>

      {/* Risk Recommendations */}
      {risk?.recommendations?.length > 0 && (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-5 mb-6">
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Risk Recommendations</p>
          <div className="space-y-2">
            {risk.recommendations.map((rec, i) => (
              <div key={i} className="flex items-start gap-3">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: rec.priority === "high" ? "#ef4444" : rec.priority === "medium" ? "#F59E0B" : "#556B2F" }} />
                <div className="flex-1">
                  <p className="text-white/70 text-xs font-sans">{rec.action}</p>
                </div>
                <span className={`text-[10px] font-sans uppercase px-2 py-0.5 rounded-sm ${rec.priority === "high" ? "text-red-400 bg-red-400/10" : rec.priority === "medium" ? "text-[#F59E0B] bg-[#F59E0B]/10" : "text-[#556B2F] bg-[#556B2F]/10"}`}>
                  {rec.priority}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lifecycle History Timeline */}
      {property.lifecycle_history?.length > 0 && (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-5">
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-4">Lifecycle Timeline</p>
          <div className="space-y-0">
            {[...property.lifecycle_history].reverse().map((entry, i) => (
              <div key={i} className="flex gap-3 pb-4 last:pb-0">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full border-2" style={{ borderColor: LIFECYCLE_COLORS[entry.state], backgroundColor: i === 0 ? LIFECYCLE_COLORS[entry.state] : "transparent" }} />
                  {i < property.lifecycle_history.length - 1 && <div className="w-px flex-1 bg-white/10 mt-1" />}
                </div>
                <div className="flex-1 -mt-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-sans text-xs font-medium">{entry.label}</span>
                    <span className="text-white/20 text-[10px] font-sans">State {entry.state}</span>
                  </div>
                  {entry.note && <p className="text-white/40 text-[10px] font-sans mt-0.5">{entry.note}</p>}
                  <p className="text-white/20 text-[10px] font-sans mt-0.5">{new Date(entry.at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full Activity Timeline */}
      <div className="mt-6">
        <PropertyTimeline propertyId={propertyId} />
      </div>

      {/* Update Lifecycle Dialog */}
      <Dialog open={showLifecycle} onOpenChange={setShowLifecycle}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Update Lifecycle State</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-white/40 text-xs font-sans">Current: <strong className="text-[#D4AF37]">{lifecycleStates[currentState]?.label}</strong> (State {currentState})</p>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger data-testid="lifecycle-state-select" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select new state" /></SelectTrigger>
              <SelectContent className="bg-[#11130F] border-white/10 max-h-60">
                {Object.entries(lifecycleStates).map(([k, v]) => (
                  <SelectItem key={k} value={k}>[{k}] {v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Note</label>
              <input value={lifecycleNote} onChange={e => setLifecycleNote(e.target.value)} placeholder="Reason for state change..." className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-white text-sm font-sans placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/40" />
            </div>
            <Button data-testid="submit-lifecycle-btn" onClick={updateLifecycle} disabled={!selectedState} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">Update State</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Badge Dialog */}
      <Dialog open={showBadge} onOpenChange={setShowBadge}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Add Trust Badge</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {availableBadges.filter(ab => !badges.find(b => b.key === ab.key)).map(ab => {
                const Icon = BADGE_ICONS[ab.icon] || Shield;
                return (
                  <button key={ab.key} onClick={() => setSelectedBadge(ab.key)}
                    className={`flex items-center gap-2 p-3 rounded-sm border text-xs font-sans transition-colors ${selectedBadge === ab.key ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-white/50 hover:border-white/20"}`}>
                    <Icon className="w-4 h-4" />
                    {ab.label}
                  </button>
                );
              })}
            </div>
            <Button data-testid="submit-badge-btn" onClick={addBadge} disabled={!selectedBadge} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">Add Badge</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
